import React, { createContext, useContext, useState, useMemo } from 'react';

// Default context value
const DEFAULT_CONTEXT = {
    initPosition: null,
    timestampComments: null,
    highlightCommentId: null,
    newPositionComment: false,
    addNotesMode: false,
    setInitPosition: () => { },
    setNewPositionComment: () => { },
    onSaved: () => { },
    startAddNotesMode: () => { },
    exitAddNotesMode: () => { },
    handleCommentClick: () => { },
    updateTimestampComments: () => { },
    highlightComment: () => { }
};

// Create the context
const InPlayerCommentContext = createContext(DEFAULT_CONTEXT);

const InPlayerCommentProvider = ({ children }) => {
    const [newPositionComment, setNewPositionComment] = useState(null);
    const [addNotesMode, setAddNotesMode] = useState(null);
    const [initPosition, setInitPosition] = useState(null);
    const [highlightCommentId, setHighlightCommentId] = useState(null);

    const [timestampComments, setTimestampComments] = useState(null);

    const onSaved = () => {
        setNewPositionComment(null);
        setInitPosition(null);
    };

    const startAddNotesMode = () => {
        window.scrollTo(0, 0);
        setTimeout(() => {
            setAddNotesMode(true);
        }, 100);
    };

    const exitAddNotesMode = () => {
        setAddNotesMode(false);
        setInitPosition(null);
    };

    const updateTimestampComments = (comments) => {
        setTimestampComments(comments);
    }

    const handleCommentClick = (comments) => {
        comments?.forEach(({ id }, index) => {
            // scroll to the first comment
            if (index === 0) {
                const commentContainer = document.getElementById(`comment-${id}`);
                commentContainer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }

            const commentContent = document.getElementById(`comment-content-${id}`);

            if (commentContent) {
                commentContent.style.transition = "border 1s ease-out";
                commentContent.style.border = "2px solid #7C43AA";

                setTimeout(() => {
                    commentContent.style.border = "2px solid transparent";
                }, 1000);
            }
        })
    };

    const highlightComment = (commentId) => {
        setHighlightCommentId(commentId);
        setTimeout(() => {
            setHighlightCommentId(null);
        }, 3000)
    }

    const value = useMemo(() => ({
        initPosition,
        highlightCommentId,
        setInitPosition,
        newPositionComment,
        setNewPositionComment,
        onSaved,
        addNotesMode,
        startAddNotesMode,
        exitAddNotesMode,
        handleCommentClick,
        timestampComments,
        updateTimestampComments,
        highlightComment,
    }), [newPositionComment, addNotesMode, initPosition, timestampComments, highlightCommentId]);

    return (
        <InPlayerCommentContext.Provider value={value}>
            {children}
        </InPlayerCommentContext.Provider>
    );
};

// Custom hook for consuming the context
const useInPlayerCommentContext = () => {
    const context = useContext(InPlayerCommentContext);
    if (!context || context === DEFAULT_CONTEXT) {
        return DEFAULT_CONTEXT; // Fallback to default value
    }
    return context;
};

export { InPlayerCommentProvider, useInPlayerCommentContext };
export default InPlayerCommentContext;
